import { useMemo } from 'react';
import { FallbackProps } from 'react-error-boundary';
import { Text } from '@antimatterhq/ui/components/typography';
import { Skeleton } from '@antimatterhq/ui/components/skeleton';
import { BaseObservablesTable } from '@/components/observables/table/BaseObservablesTable';
import { BaseHomepageSummaryCard } from '@/components/homepage/summaryCard/BaseHomepageSummaryCard';
import { useGetObservablesTop10 } from '@/hooks/dbui/useGetObservablesTop10';
import { useGetWorkspaceConfig } from '@/hooks/workspace/useGetWorkspaceConfig';
import { DefaultErrorBoundaryFallback } from '@/components/errorBoundary/ErrorBoundary';

export const ObservablesHomepageSummaryCardSkeleton = () => (
  <BaseHomepageSummaryCard
    title="Observables summary"
    to={'/explore/observables'}
  >
    <Text size="sm" className="mt-2">
      Top 5 observables by new risk associated in past 24 hours
    </Text>
    <div className="mt-4 grid grid-cols-2 gap-4">
      {[...Array(6).keys()].map((index) => (
        <div key={index} className="flex flex-col">
          <div className="flex-grow p-2">
            <Skeleton className="h-6 w-1/2" />
          </div>
          <BaseObservablesTable
            key={index}
            name=""
            items={undefined}
            maxRowCount={5}
          />
        </div>
      ))}
    </div>
  </BaseHomepageSummaryCard>
);

export const ObservablesHomepageSummaryCardErrorFallback = (
  props: FallbackProps,
) => {
  return (
    <BaseHomepageSummaryCard
      title="Observables summary"
      to="/explore/observables"
    >
      <DefaultErrorBoundaryFallback {...props} />
    </BaseHomepageSummaryCard>
  );
};

export const ObservablesHomepageSummaryCard = () => {
  // These are used in more places so I didnt use suspense yet
  const { data: config, isFetching: isFetchingWorkspaceConfig } =
    useGetWorkspaceConfig();
  const { data: top10, isPending: top10IsPending } = useGetObservablesTop10();

  const observables = useMemo(() => {
    return config?.spec?.observables?.kinds?.map(({ name }) => {
      const items = !top10IsPending
        ? (top10?.items?.find((item) => item.kind === name)?.top10 ?? [])
        : undefined;

      return {
        name,
        items,
      };
    });
  }, [config, top10?.items, top10IsPending]);

  if (isFetchingWorkspaceConfig) {
    return <ObservablesHomepageSummaryCardSkeleton />;
  }

  return (
    <BaseHomepageSummaryCard
      title="Observables summary"
      to={'/explore/observables'}
    >
      <Text size="sm" className="mt-2">
        Top 5 observables by risk
      </Text>
      <div className="mt-4 grid grid-cols-2 gap-4">
        {observables ? (
          observables?.map(({ name, items }, index) => (
            <div key={index} className="flex flex-col">
              <div className="p-2">
                <Text size="lg">{name}</Text>
              </div>
              <BaseObservablesTable
                key={name}
                name={name || ''}
                items={items}
                maxRowCount={5}
              />
            </div>
          ))
        ) : (
          <Text size="lg">No observables found</Text>
        )}
      </div>
    </BaseHomepageSummaryCard>
  );
};
